import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, inject } from '@angular/core';

import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { RadioButtonModule } from 'primeng/radiobutton';
import { TableModule } from 'primeng/table';
import { SkeletonModule } from 'primeng/skeleton';
import { TooltipModule } from 'primeng/tooltip';
import { TruncatePipe } from '../../pipes/truncate.pipe';
import { CONVERSATION_AI_TOKEN } from '../../models/agent-card-abstract.model';
import { filter } from 'rxjs';
import { DatePipe, DecimalPipe } from '@angular/common';
import { SelectModule } from 'primeng/select';
import { EModelQuality } from '@dataclouder/ngx-core';
import { SimpleAgentTask } from '../../models/agent.models';

interface ModelData {
  id: string;
  name: string;
  description: string;
}

export function createAIModelFormGroup(task?: SimpleAgentTask) {
  const fb = inject(FormBuilder);
  return fb.group({
    quality: [task?.model?.quality || EModelQuality.FAST],
    modelName: [task?.model?.modelName || ''],
    provider: [task?.model?.provider || 'google'],
    id: [task?.model?.id || ''],
  });
}

/**
 * A component that provides AI model provider selection functionality.
 * This component allows users to select an AI provider and displays available models
 * for the selected provider, you need to handle values logic in the parent component.
 * you have to pass the parent form group to the component, with controls: provider and modelName.
 *
 * @example
 * ```html
 * <dc-model-selector
 *   [parentForm]="yourFormGroup">
 * </dc-model-selector>
 * ```
 */
@Component({
  selector: 'dc-model-selector',
  standalone: true,
  imports: [ReactiveFormsModule, RadioButtonModule, TableModule, SkeletonModule, TooltipModule, TruncatePipe, DatePipe, DecimalPipe, SelectModule],
  templateUrl: './provider-selector.component.html',
  styleUrl: './provider-selector.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModelSelectorComponent implements OnInit {
  private conversationCardAIService = inject(CONVERSATION_AI_TOKEN);
  private cdr = inject(ChangeDetectorRef);

  /** Parent form group that contains provider and model selection controls */
  @Input() modelForm = createAIModelFormGroup();

  @Input() shortForm?: boolean = false;

  /** Flag indicating whether the models are currently being loaded */
  public isLoadingModels = true;

  /** Array of available models for the selected provider */
  public modelnames: ModelData[] = [];

  public providersOptions = [
    { label: 'Groq', value: 'groq' },
    { label: 'Open AI', value: 'openai' },
    { label: 'Google', value: 'google' },
    { label: 'Open Router', value: 'openrouter' },
    { label: 'Ollama', value: 'ollama' },
  ];

  /**
   * Initializes the component by setting up subscriptions to provider changes
   * and loading initial models if a provider is already selected.
   */
  ngOnInit(): void {
    console.log('modelForm', this.modelForm);
    const providerControl = this.modelForm.get('provider');

    if (providerControl) {
      providerControl.valueChanges.pipe(filter((value) => !!value)).subscribe(async (value) => {
        try {
          this.isLoadingModels = true;

          this.modelnames = (await this.conversationCardAIService.getListModels(value as string)) as ModelData[];
          console.log('modelnames', this.modelnames);
        } catch (error) {
          console.error('Error getting list models', error);
          this.modelnames = [];
        } finally {
          this.isLoadingModels = false;
          this.cdr.detectChanges();
        }
      });
    }
  }

  public modelQualityOptions = Object.values(EModelQuality).map((value) => ({
    label: value.charAt(0).toUpperCase() + value.slice(1).replace(/_/g, ' '), // Format for display
    value: value,
  }));
}
